import { isAfter, subtract } from '@portal/utils/util-date';
import {
  guardEmptyArray,
  guardEmptyString,
  guardUnspecified,
  guardZeroNumber
} from '@portal/utils/util-guards';
import { componentFactory } from 'vue-tsx-support';

import { JtnUiBtn } from '@jtnews/jtn-ui';
import {
  injectStylesMixin,
  tsBaseBlockFunctionalityMixin,
  tsStoreMixin
} from '@jtnews/shared';

import { RecordLastNewsItem } from '../record-last-news-item';
import {
  NewsParams,
  RecordLastNewsEmitParams,
  RecordLastNewsParams
} from '../record-last-news-item/record-last-news-item.component';

import styles from './record-last-news.styles.scss?module';

// eslint-disable-next-line @typescript-eslint/naming-convention
const BlockedUserModal = () =>
  import(/* webpackChunkName: "blocked-user-modal" */ '@jtnews/layout/modals').then(
    m => m.BlockedUserModal
  );

type NewFormatReachGoal = {
  goalName: string;
  actionType: string;
  blockType: string;
  prop1?: string;
  prop2?: Record<string, string>;
};

type Image = {
  id: number;
  author: string;
  description: string;
  fileMask: string;
  height: number;
  sourceName: string;
  url: string;
  width: number;
};

type Format = {
  id: number;
  name: string;
  url: string;
};

type Theme = {
  id: number;
  name: string;
  path: string;
  url: string;
};

type Rubric = {
  id: string;
  name: string;
  path: string;
};

type LastNewsRecord = {
  id: number;
  block: 'block_commercial' | 'block_not_commercial';
  header: string;
  mainPhoto: Image | null;
  formats: Format[];
  themes: Theme[];
  rubrics: Rubric[];
  subheader: string;
  urls: {
    url: string;
    urlComments: string;
    urlCanonical: string;
  };
  viewsCount: number;
  commentsCount: number;
  isCommentsAllowed: boolean;
  publishAt: string;
  timeOrder: string;
  viewType: string;
  hasVideoMark?: boolean;
};

type ComponentData = {
  excludeList: number[];
  newsFromDate: Record<string, string>;
  blockDataLength: number;
  newsList: LastNewsRecord[];
  fieldTypeReachGoal: string;
  isBlockedUserModalOpened: boolean;
  isBlockedUserModalRendered: boolean;
};

type ReachGoalParams = {
  actionType: string;
  goalName: string;
  prop1?: string;
  prop2?: Record<string, string | Record<string, string>> | string | string[];
};

export default componentFactory
  .mixin(injectStylesMixin(styles))
  .mixin(tsBaseBlockFunctionalityMixin)
  .mixin(tsStoreMixin)
  .create({
    name: 'RecordLastNews',
    data(): ComponentData {
      return {
        excludeList: [],
        newsFromDate: {},
        blockDataLength: 0,
        newsList: [],
        isBlockedUserModalOpened: false,
        isBlockedUserModalRendered: false
      };
    },
    computed: {
      canRender(): boolean {
        return guardZeroNumber(this.newsList.length);
      },
      recordId(): number {
        return (this.store.layoutModule?.record as { data: { id: number } }).data.id ?? 0;
      },
      isMobileOnly(): boolean {
        return this.store.deviceInfo.isMobile && !this.store.deviceInfo.isTablet;
      },
      isBlockedUser(): boolean {
        return this.store.commonModule.isBlockedUser;
      },
      viewReachGoals(): string[] {
        return this.blockData.reach_goal?.view || [];
      },
      isShowButton(): boolean {
        const MAX_DAYS_OF_NEWS = 10;

        return Object.values(this.newsFromDate).every(dateString => {
          const date = new Date(dateString);

          try {
            const dateTenDaysAgo = subtract(new Date(), {
              value: MAX_DAYS_OF_NEWS,
              unit: 'days'
            });

            return isAfter(date, dateTenDaysAgo);
          } catch (e) {
            return false;
          }
        });
      }
    },
    methods: {
      onVisibleBlock() {
        if (Array.isArray(this.viewReachGoals) && guardEmptyArray(this.viewReachGoals)) {
          this.viewReachGoals.forEach(name => {
            this.store.analyticsModule.sendReachGoal({ name });
          });
        }
      },
      async componentShown() {
        await this.fetchLastNews();
      },
      async fetchLastNews() {
        try {
          this.excludeList.push(this.recordId);

          const res = await this.store.pagesModule.fetchRecordLastNews({
            excludeIds: this.excludeList,
            datesFrom: this.newsFromDate
          });

          this.blockData = res.data;
          this.blockDataLength = this.blockData.data?.length || 0;
          this.newsFromDate = Object.entries(
            res.data.meta.datesFrom as Record<string, string>
          ).reduce((acc, [key, value]) => {
            if (guardEmptyString(value)) {
              acc[key] = value;
            }
            return acc;
          }, {});
          this.newsList = [
            ...this.newsList,
            ...((this.blockData.data as LastNewsRecord[] | null) || [])
          ];
          this.makeExcludeList();
        } catch (err) {
          console.error(err);
          throw err;
        }
      },
      makeExcludeList() {
        // максимальное число ID в запросе равно длине 6 блоков новостей
        const maxRequestLength = this.blockDataLength * 6;

        const showedRecords = this.newsList.map(item => item.id);

        this.excludeList =
          showedRecords.length < maxRequestLength + 1
            ? [...showedRecords]
            : [...showedRecords.slice(showedRecords.length - maxRequestLength)];
      },
      async getMoreNews() {
        await this.fetchLastNews();

        this.sendReachGoalParams({
          goalName: 'clickLatestNewsBlockML',
          actionType: `Показать ещё`
        });
      },
      sendRecordVisibleReachGoal(params: RecordLastNewsParams) {
        this.sendReachGoalParams({
          goalName: 'viewLatestNewsBlockML',
          actionType: 'Просмотр',
          prop1: `Место ${params.recordPlace}`,
          prop2: {
            [params.recordType]: {
              MN: params.recordId.toString()
            }
          }
        });
      },
      sendRecordClickedReachGoal(el: string, params: RecordLastNewsParams) {
        this.sendReachGoalParams({
          goalName: 'clickLatestNewsBlockML',
          actionType: `Клик на ${el}`,
          prop1: `Место ${params.recordPlace}`,
          prop2: {
            [params.recordType]: {
              MN: params.recordId.toString()
            }
          }
        });
      },
      sendDiscussLinkClickedReachGoal(params: RecordLastNewsEmitParams<Event>) {
        if (this.isBlockedUser) {
          params.value.preventDefault();
          this.openBlockedUserModal();
        } else {
          this.sendRecordClickedReachGoal('комментарии', params.recordParams);
        }
      },
      sendReachGoalParams(data: ReachGoalParams) {
        const { actionType, goalName, prop1, prop2 } = data;
        const blockType = 'Центральная колонка';
        const fieldType = 'Последние новости (блок)';
        const valueName = guardUnspecified(prop1)
          ? {
              [actionType]: {
                [prop1]: prop2
              }
            }
          : actionType;

        this.store.analyticsModule.sendNewReachGoal({
          blockType,
          fieldType,
          valueName,
          goalName,
          willDeprecate: true
        });
        this.store.analyticsModule.sendNewFormatReachGoal({
          blockType: fieldType,
          actionType,
          goalName,
          prop1,
          prop2
        });
      },
      openBlockedUserModal() {
        this.isBlockedUserModalOpened = true;
        this.isBlockedUserModalRendered = true;
      }
    },
    render() {
      return (
        <div v-observe-visibility={this.obsVisibilityOptions}>
          {this.canRender && [
            <ul v-observe-visibility={this.obsHalfOptions} class={styles.lastNewsBlock}>
              {this.newsList.map((record, index: number) => (
                <li class={styles.item} key={record.id}>
                  <RecordLastNewsItem
                    record={record}
                    place={index + 1}
                    isMobileOnly={this.isMobileOnly}
                    onRecordVisible={params => this.sendRecordVisibleReachGoal(params)}
                    onImageClicked={params =>
                      this.sendRecordClickedReachGoal('материал', params)
                    }
                    onHeaderClicked={params =>
                      this.sendRecordClickedReachGoal('материал', params)
                    }
                    onFormatClicked={params =>
                      this.sendRecordClickedReachGoal('формат', params.recordParams)
                    }
                    onRubricClicked={params =>
                      this.sendRecordClickedReachGoal('рубрику', params.recordParams)
                    }
                    onThemeClicked={params =>
                      this.sendRecordClickedReachGoal('тему', params.recordParams)
                    }
                    onCommentsClicked={params =>
                      this.sendRecordClickedReachGoal('комментарии', params)
                    }
                    onDiscussClicked={params =>
                      this.sendDiscussLinkClickedReachGoal(params)
                    }
                  />
                </li>
              ))}
            </ul>,
            this.isShowButton && (
              <JtnUiBtn
                theme="lightGray"
                class={styles.moreButton}
                onClick={() => void this.getMoreNews()}
              >
                показать еще
                <template slot="left">
                  <svg width="24" height="24">
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-common.f7875ac1d2b1e964bfa6c21ef5757c8a.svg#jtn-common-plus"></use>
                  </svg>
                </template>
              </JtnUiBtn>
            )
          ]}
          {this.isBlockedUserModalRendered && (
            <BlockedUserModal vModel={this.isBlockedUserModalOpened} />
          )}
        </div>
      );
    }
  });
